/* eslint-disable camelcase */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  CustomDialogProps,
  SmartAutoComplete,
  SmartForm,
  SmartSelect,
  SmartSubmit,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MapAreaItemDto,
  MapAreaTypeEnum,
  useMapAreaApi,
  useReimariApi,
} from "~/api/GeneratedApi";
import LoadingBox from "~/components/LoadingBox";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

interface FormProps {
  name: string | null;
  mapAreaType: number | null;
  reimariTSdGroupSDGROUP_IDs: number[];
  projectId: string;
  projectName: string;
}

interface Props {
  onSubmitted?: () => void;
  editableData?: MapAreaItemDto;
}

const useStyles = makeStyles(() => ({
  select: {
    minWidth: 120,
    width: "100%",
  },
}));

const MapAreaCreationDialog = ({
  handleClose,
  onSubmitted,
  editableData,
}: CustomDialogProps & Props): JSX.Element => {
  const classes = useStyles();
  const mapAreaApi = useMapAreaApi();
  const { getSdGroups } = useReimariApi();
  const { t } = useTranslation("map-area-creation");
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = useMemo(
    () => ({
      name: editableData?.name ?? "",
      mapAreaType:
        editableData?.mapAreaType ?? MapAreaTypeEnum.MaintenanceContract,
      reimariTSdGroupSDGROUP_IDs:
        editableData?.reimariTSdGroupSDGROUP_IDs ?? [],
      projectId: editableData?.projectID ?? "",
      projectName: editableData?.projectName ?? "",
    }),
    [
      editableData?.name,
      editableData?.mapAreaType,
      editableData?.reimariTSdGroupSDGROUP_IDs,
      editableData?.projectID,
      editableData?.projectName,
    ],
  );

  const methods = useSmartForm<FormProps>({
    defaultValues,
  });

  const selectedType = methods.watch("mapAreaType");

  const onSuccess = () => {
    enqueueSnackbar(t(editableData ? "edited" : "created"), {
      variant: "success",
    });
    onSubmitted?.();
    handleClose?.();
  };

  const onSubmit = (data: FormProps) => {
    if (editableData) {
      return mapAreaApi.editMapArea(
        {
          name: data.name!,
          type: data.mapAreaType!,
          reimariTSdGroupSDGROUP_IDs: data.reimariTSdGroupSDGROUP_IDs ?? [],
          projectID: data.projectId,
          projectName: data.projectName,
        },
        editableData.mapAreaId,
      );
    }

    return mapAreaApi.addMapArea({
      name: data.name!,
      type: data.mapAreaType!,
      reimariTSdGroupSDGROUP_IDs: data.reimariTSdGroupSDGROUP_IDs ?? [],
      projectID: data.projectId,
      projectName: data.projectName,
    });
  };

  const { data: sdGroups, isLoading } = useCustomQuery(
    "reimari-sd-groups",
    () => getSdGroups(),
  );

  if (isLoading) return <LoadingBox />;

  return (
    <Dialog open onClose={handleClose} aria-labelledby="form-dialog-title">
      <SmartForm<FormProps, unknown>
        onSubmit={onSubmit}
        noGrid
        onSuccess={onSuccess}
        methods={methods}
      >
        <DialogTitle id="form-dialog-title">
          {t(editableData ? "edit-title" : "title")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmartTextField<FormProps>
                fullWidth
                name="name"
                label={t("name")}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <SmartSelect<FormProps>
                formControlProps={{ className: classes.select }}
                autoWidth
                name="mapAreaType"
                label={t("map-area-type")}
                options={Object.keys(MapAreaTypeEnum)
                  .filter((k) => Number.isInteger(Number(k)))
                  .map((x) => ({
                    label: t(`map-area-type-enum:${MapAreaTypeEnum[+x]}`),
                    value: +x,
                  }))}
                controllerProps={{
                  rules: {
                    required: t("form:required").toString(),
                  },
                }}
              />
            </Grid>
            {selectedType === MapAreaTypeEnum.Project ? null : (
              <Grid item xs={12}>
                <SmartAutoComplete<FormProps, number, true>
                  name="reimariTSdGroupSDGROUP_IDs"
                  label={t("sd-group")}
                  tooltip={t("sd-group-tooltip").toString()}
                  options={sdGroups?.map((x) => x.id) ?? []}
                  fullWidth
                  multiple
                  getOptionLabel={(opt) => {
                    const group = sdGroups?.find((x) => x.id === opt);
                    if (!group) return t("general:unknown");
                    return group.name;
                  }}
                />
              </Grid>
            )}

            {selectedType === MapAreaTypeEnum.Project ? (
              <>
                <Grid item xs={12}>
                  <SmartTextField<FormProps>
                    fullWidth
                    name="projectId"
                    label={t("project-id")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SmartTextField<FormProps>
                    fullWidth
                    name="projectName"
                    label={t("project-name")}
                  />
                </Grid>
              </>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("general:cancel")}
          </Button>
          <SmartSubmit
            label={t(editableData ? "general:edit" : "general:create")}
          />
        </DialogActions>
      </SmartForm>
    </Dialog>
  );
};

export default MapAreaCreationDialog;
